import { ApiError } from '@/api/client';
import { useMasterListLessons, useUpdateLesson } from '@/api/lessons';
/**
 * Master List detail + item editor (frontend-map §1.4 — LIBRARY-UI owned, T09).
 *
 * Header: inline-editable name/description, Duplicate, and Delete-list. Items:
 * the `MasterListItemEditor` (add / edit / delete / reorder with optimistic
 * TanStack Query updates). On a 404 (list absent or not owned — read rule
 * api-contract §0.6) we render a not-found state with a link back to /library.
 */
import {
  useCreateMasterListShare,
  useDeleteMasterListShare,
  useMasterList,
  useMasterListItems,
  useMasterListShares,
  useUpdateMasterListShare,
} from '@/api/masterLists';
import { useMe } from '@/api/me';
import { MasterListHeader } from '@/components/library/MasterListHeader';
import { MasterListItemEditor } from '@/components/library/MasterListItemEditor';
import { Skeleton } from '@/components/library/ui/Skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type Lesson, type MasterListShareWithUser, ShareRole } from '@ultimate-checklist/shared';
import { ArrowLeft, CheckCircle2, Lightbulb, Trash2, Users, XCircle } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/library/$listId')({
  component: MasterListDetail,
});

function MasterListDetail() {
  const { listId } = Route.useParams();
  const list = useMasterList(listId);
  const items = useMasterListItems(listId);
  const me = useMe();
  useDocumentTitle(list.data?.name);

  const notFound = list.error instanceof ApiError && list.error.code === 'NOT_FOUND';
  const isOwner = Boolean(list.data && me.data?.id === list.data.ownerId);
  const isSharedReadOnly = Boolean(list.data && me.data?.id && me.data.id !== list.data.ownerId);

  return (
    <div className="flex flex-col gap-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2 self-start">
        <Link to="/library">
          <ArrowLeft />
          Library
        </Link>
      </Button>

      {list.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-9 w-1/2" />
          <Skeleton className="h-9 w-3/4" />
        </div>
      ) : notFound ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <h2 className="text-lg font-semibold">List not found</h2>
          <p className="max-w-sm text-sm text-[var(--muted-foreground)]">
            This list doesn’t exist or isn’t available to you.
          </p>
          <Button asChild variant="outline">
            <Link to="/library">Back to Library</Link>
          </Button>
        </div>
      ) : list.isError || !list.data ? (
        <div className="flex flex-col items-center gap-4 py-16 text-center">
          <h2 className="text-lg font-semibold">Couldn’t load this list</h2>
          <Button asChild variant="outline">
            <Link to="/library">Back to Library</Link>
          </Button>
        </div>
      ) : (
        <>
          {isSharedReadOnly ? (
            <Card>
              <CardContent className="flex flex-col gap-2 p-4 text-sm text-[var(--muted-foreground)] sm:flex-row sm:items-center sm:justify-between">
                <span>
                  This shared list is read-only here. Duplicate it to customize your own copy.
                </span>
              </CardContent>
            </Card>
          ) : null}
          <MasterListHeader list={list.data} readOnly={isSharedReadOnly} />
          <MasterListItemEditor
            list={list.data}
            listId={listId}
            items={items.data}
            isLoading={items.isLoading}
            isError={items.isError}
            readOnly={isSharedReadOnly}
          />
          {isOwner ? <MasterListLessonsPanel listId={listId} /> : null}
          {isOwner ? <MasterListSharingPanel listId={listId} /> : null}
        </>
      )}
    </div>
  );
}

function MasterListLessonsPanel({ listId }: { listId: string }) {
  const lessons = useMasterListLessons(listId, 'open');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lightbulb className="h-4 w-4" />
          Lessons
        </CardTitle>
        <CardDescription>Review trip takeaways before changing this template.</CardDescription>
      </CardHeader>
      <CardContent>
        {lessons.isLoading ? (
          <Skeleton className="h-16" />
        ) : lessons.isError ? (
          <p className="text-sm text-[var(--muted-foreground)]">Couldn’t load lessons.</p>
        ) : !lessons.data?.length ? (
          <p className="text-sm text-[var(--muted-foreground)]">No open lessons for this list.</p>
        ) : (
          <div className="divide-y divide-[var(--border)] rounded-md border border-[var(--border)]">
            {lessons.data.map((lesson) => (
              <LessonReviewRow key={lesson.id} lesson={lesson} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LessonReviewRow({ lesson }: { lesson: Lesson }) {
  const updateLesson = useUpdateLesson();
  const [text, setText] = useState(lesson.text);

  useEffect(() => setText(lesson.text), [lesson.text]);

  async function applyLesson() {
    try {
      await updateLesson.mutateAsync({ lessonId: lesson.id, body: { text, status: 'applied' } });
      toast.success('Lesson applied');
    } catch {
      toast.error('Couldn’t apply lesson');
    }
  }

  async function dismissLesson() {
    try {
      await updateLesson.mutateAsync({ lessonId: lesson.id, body: { text, status: 'dismissed' } });
      toast.success('Lesson dismissed');
    } catch {
      toast.error('Couldn’t dismiss lesson');
    }
  }

  return (
    <div className="grid gap-3 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="rounded-md bg-[var(--muted)] px-2 py-1 text-xs font-medium">
          {lesson.type === 'forgot'
            ? 'Forgot'
            : lesson.type === 'didnt_need'
              ? 'Didn’t need'
              : 'Note'}
        </span>
        <span className="text-xs text-[var(--muted-foreground)]">
          {new Date(lesson.createdAt).toLocaleDateString()}
        </span>
      </div>
      <Input
        value={text}
        onChange={(event) => setText(event.target.value)}
        aria-label="Lesson text"
        maxLength={2000}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={updateLesson.isPending || !text.trim()}
          onClick={() => void applyLesson()}
        >
          <CheckCircle2 />
          Apply
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={updateLesson.isPending}
          onClick={() => void dismissLesson()}
        >
          <XCircle />
          Dismiss
        </Button>
      </div>
    </div>
  );
}

function MasterListSharingPanel({ listId }: { listId: string }) {
  const shares = useMasterListShares(listId);
  const createShare = useCreateMasterListShare(listId);
  const updateShare = useUpdateMasterListShare(listId);
  const deleteShare = useDeleteMasterListShare(listId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MasterListShareWithUser['role']>('viewer');

  async function onInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsedRole = ShareRole.parse(role);
    try {
      await createShare.mutateAsync({ email, role: parsedRole });
      setEmail('');
      setRole('viewer');
      toast.success('List shared');
    } catch {
      toast.error('Couldn’t share list');
    }
  }

  async function onRoleChange(share: MasterListShareWithUser, nextRole: string) {
    const parsedRole = ShareRole.safeParse(nextRole);
    if (!parsedRole.success || parsedRole.data === share.role) {
      return;
    }
    try {
      await updateShare.mutateAsync({ shareId: share.id, body: { role: parsedRole.data } });
      toast.success('Share updated');
    } catch {
      toast.error('Couldn’t update share');
    }
  }

  async function onRemove(shareId: string) {
    try {
      await deleteShare.mutateAsync(shareId);
      toast.success('Share removed');
    } catch {
      toast.error('Couldn’t remove share');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4" />
          Sharing
        </CardTitle>
        <CardDescription>Invite another account to view or edit this master list.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form onSubmit={onInvite} className="grid gap-3 sm:grid-cols-[1fr_8rem_auto]">
          <div className="grid gap-1">
            <Label htmlFor="share-email">Email</Label>
            <Input
              id="share-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="friend@example.com"
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="share-role">Role</Label>
            <select
              id="share-role"
              value={role}
              onChange={(event) => setRole(ShareRole.parse(event.target.value))}
              className="h-10 rounded-md border border-[var(--input)] bg-[var(--background)] px-3 text-sm"
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
            </select>
          </div>
          <Button
            type="submit"
            className="self-end"
            disabled={createShare.isPending || email.trim().length === 0}
          >
            Invite
          </Button>
        </form>

        {shares.isLoading ? (
          <Skeleton className="h-14" />
        ) : shares.isError ? (
          <p className="text-sm text-[var(--muted-foreground)]">Couldn’t load shares.</p>
        ) : !shares.data?.length ? (
          <p className="text-sm text-[var(--muted-foreground)]">No one else has access yet.</p>
        ) : (
          <div className="divide-y divide-[var(--border)] rounded-md border border-[var(--border)]">
            {shares.data.map((share) => (
              <div
                key={share.id}
                className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{share.displayName || share.email}</p>
                  <p className="truncate text-xs text-[var(--muted-foreground)]">{share.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={share.role}
                    onChange={(event) => void onRoleChange(share, event.target.value)}
                    className="h-9 rounded-md border border-[var(--input)] bg-[var(--background)] px-2 text-sm"
                    disabled={updateShare.isPending}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-[var(--destructive)]"
                    disabled={deleteShare.isPending}
                    onClick={() => void onRemove(share.id)}
                  >
                    <Trash2 />
                    <span className="sr-only">Remove share</span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
